<script setup lang="ts">
import { SQUARE_KEYS } from '~/constants/pictures'

const { crumbs } = useBreadcrumbs()

const { t } = useI18n()
useSeoText({ title: t('pages.how_it_works.meta.title'), description: t('pages.how_it_works.meta.description') })
const { getTranslatedLink } = useGetTranslatedLink()
</script>

<template>
  <div>
    <AppPageHeader
      :image="SQUARE_KEYS"
      class="mb-12"
    >
      <template #breadcrumb>
        <YscBreadcrumbs :crumbs="crumbs">
          <template #default="{ crumb }">
            <NuxtLink
              :to="{ name: crumb.name }"
              class="focus-visible:ring-peacock-500 font-light hover:underline focus-visible:rounded focus-visible:ring-2 group-last:font-semibold"
              itemprop="item"
            >
              <span itemprop="name">
                {{ crumb.translatedName }}
              </span>
            </NuxtLink>
          </template>
        </YscBreadcrumbs>
      </template>

      <template #title>
        {{ getTranslatedLink('how-yescapa-works') }}
      </template>

      <template #subtitle>
        {{ $t('pages.how_it_works.subtitle') }}
      </template>
    </AppPageHeader>

    <div class="bg-gray-50">
      <div class="container">
        <p class="text-peacock-500 py-6 font-semibold uppercase md:text-center">
          {{ $t('pages.how_it_works.timeline.discover.title') }}
        </p>

        <AppTimelineItem>
          <template #left>
            <AppTimelineItemContent has-search-link>
              <template #user-type>
                {{ $t('commons.traveler') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.discover.find.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.discover.find.description') }}
              </template>
              <template #tip>
                {{ $t('pages.how_it_works.timeline.discover.find.tip') }}
              </template>
            </AppTimelineItemContent>
          </template>

          <template #right>
            <AppTimelineItemContent has-onboarding-link>
              <template #user-type>
                {{ $t('commons.owner') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.discover.register.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.discover.register.description') }}
              </template>
              <template #tip>
                {{ $t('pages.how_it_works.timeline.discover.register.tip') }}
              </template>
            </AppTimelineItemContent>
          </template>
        </AppTimelineItem>
      </div>
    </div>

    <div class="container">
      <p class="text-peacock-500 py-6 font-semibold uppercase md:text-center">
        {{ $t('pages.how_it_works.timeline.linking.title') }}
      </p>
      <AppTimelineItem>
        <template #left>
          <AppTimelineItemContent>
            <template #user-type>
              {{ $t('commons.traveler') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.linking.request.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.linking.request.description') }}
            </template>
            <template #tip>
              {{ $t('pages.how_it_works.timeline.linking.request.tip') }}
            </template>
          </AppTimelineItemContent>
        </template>
      </AppTimelineItem>

      <AppTimelineItem>
        <template #right>
          <AppTimelineItemContent>
            <template #user-type>
              {{ $t('commons.owner') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.linking.answer.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.linking.answer.description') }}
            </template>
            <template #tip>
              {{ $t('pages.how_it_works.timeline.linking.answer.tip') }}
            </template>
          </AppTimelineItemContent>
        </template>
      </AppTimelineItem>

      <AppTimelineItem>
        <template #left>
          <AppTimelineItemContent>
            <template #user-type>
              {{ $t('commons.traveler') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.linking.confirm.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.linking.confirm.description') }}
            </template>
            <template #tip>
              {{ $t('pages.how_it_works.timeline.linking.confirm.tip') }}
            </template>
          </AppTimelineItemContent>
        </template>
      </AppTimelineItem>
    </div>

    <div class="bg-gray-50">
      <div class="container">
        <p class="text-peacock-500 py-6 font-semibold uppercase md:text-center">
          {{ $t('pages.how_it_works.timeline.start.title') }}
        </p>
        <AppTimelineItem>
          <template #left>
            <AppTimelineItemContent>
              <template #user-type>
                {{ $t('commons.traveler') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.start.pick_up.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.start.pick_up.description') }}
              </template>
            </AppTimelineItemContent>
          </template>
          <template #right>
            <AppTimelineItemContent>
              <template #user-type>
                {{ $t('commons.owner') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.start.meet.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.start.meet.description') }}
              </template>
            </AppTimelineItemContent>
          </template>
        </AppTimelineItem>

        <AppTimelineItem>
          <template #left>
            <AppTimelineItemContent>
              <template #user-type>
                {{ $t('commons.traveler') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.start.go.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.start.go.description') }}
              </template>
            </AppTimelineItemContent>
          </template>
          <template #right>
            <AppTimelineItemContent>
              <template #user-type>
                {{ $t('commons.owner') }}
              </template>
              <template #title>
                {{ $t('pages.how_it_works.timeline.start.relax.title') }}
              </template>
              <template #description>
                {{ $t('pages.how_it_works.timeline.start.relax.description') }}
              </template>
              <template #tip>
                {{ $t('pages.how_it_works.timeline.start.relax.tip') }}
              </template>
            </AppTimelineItemContent>
          </template>
        </AppTimelineItem>
      </div>
    </div>

    <div class="container">
      <p class="text-peacock-500 py-6 font-semibold uppercase md:text-center">
        {{ $t('commons.trip_back') }}
      </p>
      <AppTimelineItem>
        <template #left>
          <AppTimelineItemContent>
            <template #user-type>
              {{ $t('commons.traveler') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.return.end.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.return.end.description') }}
            </template>
          </AppTimelineItemContent>
        </template>
        <template #right>
          <AppTimelineItemContent>
            <template #user-type>
              {{ $t('commons.owner') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.return.pick_up.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.return.pick_up.description') }}
            </template>
          </AppTimelineItemContent>
        </template>
      </AppTimelineItem>

      <AppTimelineItem>
        <template #left>
          <AppTimelineItemContent has-search-link>
            <template #user-type>
              {{ $t('commons.traveler') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.return.review_guest.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.return.review_guest.description') }}
            </template>
            <template #tip>
              {{ $t('pages.how_it_works.timeline.return.review_guest.tip') }}
            </template>
          </AppTimelineItemContent>
        </template>
        <template #right>
          <AppTimelineItemContent has-onboarding-link>
            <template #user-type>
              {{ $t('commons.owner') }}
            </template>
            <template #title>
              {{ $t('pages.how_it_works.timeline.return.review_owner.title') }}
            </template>
            <template #description>
              {{ $t('pages.how_it_works.timeline.return.review_owner.description') }}
            </template>
            <template #tip>
              {{ $t('pages.how_it_works.timeline.return.review_owner.tip') }}
            </template>
          </AppTimelineItemContent>
        </template>
      </AppTimelineItem>
    </div>
  </div>
</template>
